import React from "react";
import { Send, ChevronLeft, NotebookText } from "lucide-react";
import UserAvatar from "./UserAvatar";
import MessageBubble from "./MessageBubble";

const formatTimeLabel = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const MessagesColumn = ({
  mobileSectionShown,
  setMobileSectionShown,
  selectedConversation,
  currentUserId,
  loadingMessages,
  groupedMessages,
  messageBoxRef,
  messageContentRef,
  newMessageText,
  setNewMessageText,
  sending,
  handleSend,
  handleKeyDown,
}) => (
  <div
    className={`${mobileSectionShown === "messages" ? "flex" : "hidden"} lg:flex flex-1 flex-col bg-gray-50 dark:bg-darkBg2`}
  >
    {selectedConversation ? (
      <>
        {/* Header */}
        <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 border-b border-gray-200 dark:border-gray-700 bg-[#ffffff] dark:bg-darkBg1">
          <button
            onClick={() => setMobileSectionShown("conversations")}
            className="flex lg:hidden items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex-shrink-0"
          >
            <ChevronLeft size={16} />
          </button>
          {(() => {
            const other = selectedConversation.participants?.find(
              (p) => p._id?.toString() !== currentUserId?.toString(),
            );
            return (
              <>
                <UserAvatar user={other} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-xs md:text-sm dark:text-white">
                    {other
                      ? `${other.firstName || ""} ${other.lastName || ""}`.trim()
                      : "Unknown User"}
                  </p>
                  <p className="text-[10px] md:text-xs text-gray-400">
                    Support conversation
                  </p>
                </div>
              </>
            );
          })()}
          <button
            onClick={() => setMobileSectionShown("reservation")}
            className="flex lg:hidden items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 flex-shrink-0"
          >
            <NotebookText size={16} color="#2F5FFF" />
          </button>
        </div>

        {/* Messages area */}
        <div ref={messageBoxRef} className="flex-1 overflow-y-auto py-2">
          <div ref={messageContentRef}>
            {loadingMessages ? (
              <div className="flex items-center justify-center h-32 text-sm text-gray-400">
                Loading messages...
              </div>
            ) : Object.keys(groupedMessages).length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-gray-400">
                No messages yet. Start the conversation!
              </div>
            ) : (
              Object.entries(groupedMessages).map(([date, msgs]) => (
                <div key={date}>
                  <div className="flex items-center justify-center my-4">
                    <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                      {formatTimeLabel(date)}
                    </span>
                  </div>
                  {msgs.map((msg) => (
                    <MessageBubble
                      key={msg._id}
                      message={msg}
                      currentUserId={currentUserId}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Input area */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-[#ffffff] dark:bg-darkBg1">
          <div className="flex items-center gap-2 md:gap-3 border border-gray-200 dark:border-gray-600 rounded-full px-3 md:px-4 py-1.5 md:py-2 bg-gray-50 dark:bg-darkBg2">
            <textarea
              rows={1}
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              className="flex-1 resize-none border-0 bg-transparent outline-none text-xs md:text-sm placeholder:text-gray-400 dark:text-white max-h-20 min-h-5 md:min-h-6"
            />
            <button
              onClick={handleSend}
              disabled={sending || !newMessageText.trim()}
              className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full bg-primaryColor text-white disabled:opacity-40 hover:opacity-90 transition flex-shrink-0"
            >
              {sending ? (
                <svg
                  className="animate-spin w-4 h-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
              ) : (
                <Send size={16} />
              )}
            </button>
          </div>
          <p className="text-[10px] md:text-xs text-gray-400 mt-1 pl-1 hidden sm:block">
            Press Enter to send · Shift+Enter for new line
          </p>
        </div>
      </>
    ) : (
      <div className="flex items-center justify-center flex-1 text-gray-400 text-sm">
        Select a conversation to start chatting
      </div>
    )}
  </div>
);

export default MessagesColumn;
